/**
 * AI CONVERSATION PANEL
 *
 * Central chat view showing AI-to-AI conversations (Codex, Claude Code).
 * Clean, minimal Cursor-style design.
 */

import { useState, useRef, useEffect, useMemo } from "react";
import { useTimeoutRef } from "@/hooks/useTimeoutRef";
import { type ChatThread, type ChatMessage, type ChatMessageRole, type AIModel } from "@/stores/reviewStore";
import { ActivityBox, GroupedActivityBox } from "./ActivityBox";
import { useAiChatStore } from "@/stores/aiChatStore";
import { useWorldStore } from "@/stores/worldStore";
import type { AiChatSession, DerivedMessage } from "@/types/ai-chat";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  User,
  Pause,
  Play,
  Copy,
  Check,
  FileCode,
  MessageCircle,
  Sparkles,
  ChevronDown,
  File,
  Terminal,
  Zap,
  FolderOpen,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ClaudeLogo, ChatGPTLogo, CodexLogo, GeminiLogo, LocalModelLogo } from "@/components/icons/AILogos";

// ── Reasoning levels ────────────────────────────────────────────────

type ReasoningLevel = "standard" | "high";

// ── Model configuration ─────────────────────────────────────────────

type ModelConfigItem = {
  label: string;
  shortLabel: string;
  Logo: React.ComponentType<{ size?: number; className?: string }>;
  vendor: "anthropic" | "openai" | "google" | "local";
  supportsReasoning?: boolean;
};

// Selectable models (shown in the picker, ordered by preference)
const selectableModels: AIModel[] = [
  "claude-opus-4.6",
  "claude-sonnet-4.5",
  "claude-haiku-4.5",
  "codex-5.3",
  "o3",
  "gpt-4o",
  "gemini-2.5-pro",
  "gemini-2.5-flash",
  "claude-code",
  "local",
];

const modelConfig: Record<string, ModelConfigItem> = {
  "claude-opus-4.6": {
    label: "Claude Opus 4.6",
    shortLabel: "Opus 4.6",
    Logo: ClaudeLogo,
    vendor: "anthropic",
    supportsReasoning: true,
  },
  "claude-sonnet-4.5": {
    label: "Claude Sonnet 4.5",
    shortLabel: "Sonnet 4.5",
    Logo: ClaudeLogo,
    vendor: "anthropic",
    supportsReasoning: true,
  },
  "claude-haiku-4.5": {
    label: "Claude Haiku 4.5",
    shortLabel: "Haiku 4.5",
    Logo: ClaudeLogo,
    vendor: "anthropic",
  },
  "codex-5.3": {
    label: "Codex 5.3",
    shortLabel: "Codex 5.3",
    Logo: CodexLogo,
    vendor: "openai",
    supportsReasoning: true,
  },
  o3: {
    label: "o3",
    shortLabel: "o3",
    Logo: ChatGPTLogo,
    vendor: "openai",
    supportsReasoning: true,
  },
  "gpt-4o": {
    label: "GPT-4o",
    shortLabel: "GPT-4o",
    Logo: ChatGPTLogo,
    vendor: "openai",
  },
  "gemini-2.5-pro": {
    label: "Gemini 2.5 Pro",
    shortLabel: "Gemini Pro",
    Logo: GeminiLogo,
    vendor: "google",
    supportsReasoning: true,
  },
  "gemini-2.5-flash": {
    label: "Gemini 2.5 Flash",
    shortLabel: "Gemini Flash",
    Logo: GeminiLogo,
    vendor: "google",
  },
  "claude-code": {
    label: "Claude Code",
    shortLabel: "Claude Code",
    Logo: ClaudeLogo,
    vendor: "anthropic",
  },
  local: {
    label: "Local Model",
    shortLabel: "Local",
    Logo: LocalModelLogo,
    vendor: "local",
  },
  // Legacy entries (for existing conversation data)
  claude: {
    label: "Claude",
    shortLabel: "Claude",
    Logo: ClaudeLogo,
    vendor: "anthropic",
  },
  codex: {
    label: "Codex",
    shortLabel: "Codex",
    Logo: CodexLogo,
    vendor: "openai",
  },
  "gpt-4": {
    label: "GPT-4",
    shortLabel: "GPT-4",
    Logo: ChatGPTLogo,
    vendor: "openai",
  },
  "gpt-3.5": {
    label: "GPT-3.5",
    shortLabel: "GPT-3.5",
    Logo: ChatGPTLogo,
    vendor: "openai",
  },
  gemini: {
    label: "Gemini",
    shortLabel: "Gemini",
    Logo: GeminiLogo,
    vendor: "google",
  },
};

function getModelConfig(model: string): ModelConfigItem {
  return modelConfig[model] || modelConfig["claude-opus-4.6"];
}

// ── Context item type ───────────────────────────────────────────────

interface ContextItem {
  id: string;
  type: "project" | "file";
  label: string;
}

// ── Code Block ──────────────────────────────────────────────────────

interface CodeBlockProps {
  language: string;
  code: string;
  filename?: string;
}

function CodeBlock({ language, code, filename }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useTimeoutRef();

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
  };

  const formatCodeWithLinks = (text: string) => {
    const splitRegex = /(https?:\/\/[^\s]+)/g;
    const testRegex = /^https?:\/\/[^\s]+$/;
    const parts = text.split(splitRegex);
    return parts.map((part, i) => {
      if (testRegex.test(part)) {
        return (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#00B2C8] hover:underline"
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  return (
    <div
      className="my-2 rounded-[8px] overflow-hidden border border-[var(--colony-border)] w-full"
      style={{ backgroundColor: "var(--colony-surface)" }}
    >
      <div
        className="flex items-center justify-between px-3 py-1 border-b border-[var(--colony-border)]"
        style={{ backgroundColor: "var(--colony-surface-elevated)" }}
      >
        <div className="flex items-center gap-2 text-[11px]" style={{ color: "var(--colony-text-tertiary)" }}>
          <Terminal className="w-3.5 h-3.5" style={{ color: "var(--colony-brand)" }} />
          {filename && <span className="font-medium" style={{ color: "var(--colony-text-secondary)" }}>{filename}</span>}
          {filename && language && <span style={{ color: "var(--colony-text-tertiary)" }}>·</span>}
          <span>{language}</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          className="h-5 px-2 text-[10px] hover:bg-[var(--colony-surface-hover)]"
          style={{ color: "var(--colony-text-tertiary)" }}
        >
          {copied ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
      <pre
        className="px-3 py-2 overflow-x-auto text-[13px] font-mono leading-snug max-h-[180px]"
        style={{ color: "var(--colony-text-secondary)" }}
      >
        <code>{formatCodeWithLinks(code)}</code>
      </pre>
    </div>
  );
}

// ── Simple Markdown Renderer ────────────────────────────────────────

function SimpleMarkdown({ text }: { text: string }) {
  const lines = text.split("\n");
  const elements: React.ReactNode[] = [];
  let i = 0;

  const renderInline = (line: string): React.ReactNode[] => {
    // Bold, inline code, links
    const parts: React.ReactNode[] = [];
    const regex = /(\*\*(.+?)\*\*|`([^`]+)`|(https?:\/\/[^\s)]+))/g;
    let lastIdx = 0;
    let match;
    let key = 0;
    while ((match = regex.exec(line)) !== null) {
      if (match.index > lastIdx) {
        parts.push(line.slice(lastIdx, match.index));
      }
      if (match[2]) {
        parts.push(<strong key={key++} className="font-semibold">{match[2]}</strong>);
      } else if (match[3]) {
        parts.push(
          <code key={key++} className="px-1 py-0.5 rounded text-[12px] font-mono bg-[var(--colony-surface-hover)]">
            {match[3]}
          </code>
        );
      } else if (match[4]) {
        parts.push(
          <a key={key++} href={match[4]} target="_blank" rel="noopener noreferrer"
            className="text-[var(--colony-brand)] hover:underline">{match[4]}</a>
        );
      }
      lastIdx = match.index + match[0].length;
    }
    if (lastIdx < line.length) {
      parts.push(line.slice(lastIdx));
    }
    return parts.length > 0 ? parts : [line];
  };

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // Empty line → spacer
    if (!trimmed) {
      elements.push(<div key={i} className="h-2" />);
      i++;
      continue;
    }

    // Headers
    if (trimmed.startsWith("### ")) {
      elements.push(<h4 key={i} className="text-[13px] font-semibold mt-2 mb-1" style={{ color: "var(--colony-text-primary)" }}>{renderInline(trimmed.slice(4))}</h4>);
      i++;
      continue;
    }
    if (trimmed.startsWith("## ")) {
      elements.push(<h3 key={i} className="text-[14px] font-semibold mt-3 mb-1" style={{ color: "var(--colony-text-primary)" }}>{renderInline(trimmed.slice(3))}</h3>);
      i++;
      continue;
    }

    // Markdown table
    if (trimmed.startsWith("|") && trimmed.endsWith("|")) {
      const tableRows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        const row = lines[i].trim();
        // Skip separator rows (|---|---|)
        if (/^\|[\s-:|]+\|$/.test(row)) {
          i++;
          continue;
        }
        const cells = row.split("|").slice(1, -1).map(c => c.trim());
        tableRows.push(cells);
        i++;
      }
      if (tableRows.length > 0) {
        const [header, ...body] = tableRows;
        elements.push(
          <div key={`table-${i}`} className="my-2 overflow-x-auto rounded-[6px] border border-[var(--colony-border)]">
            <table className="w-full text-[12px]">
              <thead>
                <tr style={{ backgroundColor: "var(--colony-surface-elevated)" }}>
                  {header.map((cell, ci) => (
                    <th key={ci} className="px-2 py-1.5 text-left font-medium border-b border-[var(--colony-border)]"
                      style={{ color: "var(--colony-text-secondary)" }}>
                      {renderInline(cell)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {body.map((row, ri) => (
                  <tr key={ri} className="border-b border-[var(--colony-border)] last:border-b-0">
                    {row.map((cell, ci) => (
                      <td key={ci} className="px-2 py-1.5" style={{ color: "var(--colony-text-primary)" }}>
                        {renderInline(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      continue;
    }

    // Horizontal rule
    if (/^---+\s*$/.test(trimmed)) {
      elements.push(<hr key={i} className="my-2 border-[var(--colony-border)]" />);
      i++;
      continue;
    }

    // List items
    if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
      elements.push(
        <div key={i} className="flex gap-1.5 ml-1">
          <span className="shrink-0 mt-[2px]" style={{ color: "var(--colony-text-tertiary)" }}>•</span>
          <span>{renderInline(trimmed.slice(2))}</span>
        </div>
      );
      i++;
      continue;
    }

    // Numbered lists (e.g. "1. Item")
    const numberedMatch = trimmed.match(/^(\d+)\.\s(.+)/);
    if (numberedMatch) {
      elements.push(
        <div key={i} className="flex gap-1.5 ml-1">
          <span className="shrink-0 text-[12px] font-medium tabular-nums min-w-[1.2em] text-right" style={{ color: "var(--colony-text-tertiary)" }}>{numberedMatch[1]}.</span>
          <span>{renderInline(numberedMatch[2])}</span>
        </div>
      );
      i++;
      continue;
    }

    // Regular paragraph
    elements.push(<p key={i}>{renderInline(trimmed)}</p>);
    i++;
  }

  return <>{elements}</>;
}

// ── Message Bubble ──────────────────────────────────────────────────

interface MessageBubbleProps {
  message: ChatMessage;
}

function MessageBubble({ message }: MessageBubbleProps) {
  const isUser = message.role === "user";
  const isSystem = message.role === "system";
  const model = message.model || "claude-opus-4.6";
  const config = getModelConfig(model);
  const Logo = config.Logo;

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  if (isSystem) {
    return (
      <div className="flex justify-center py-2">
        <div className="px-3 py-1.5 text-xs text-muted-foreground/60 italic">
          {message.content}
        </div>
      </div>
    );
  }

  if (isUser) {
    return (
      <div className="py-2">
        <div className="flex items-start gap-2">
          <div className="w-[20px] h-[20px] rounded-full bg-[var(--colony-brand)] flex items-center justify-center shrink-0 mt-0.5">
            <User className="w-2.5 h-2.5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[12px] font-semibold text-[var(--colony-text-primary)]">You</span>
              <span className="text-[11px] text-[var(--colony-text-tertiary)]">{formatTime(message.timestamp)}</span>
            </div>
            <div className="text-[13px] text-[var(--colony-text-primary)] leading-[1.6] whitespace-pre-wrap">
              {message.content}
            </div>
            {message.codeBlocks?.map((block, i) => (
              <CodeBlock key={i} language={block.language} code={block.code} filename={block.filename} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Detect if content has markdown formatting (headers, tables, bold, lists, numbered lists, hr)
  const hasMarkdown = /^#{1,3}\s|^\|.+\|$|\*\*[^*]+\*\*|^[-*]\s|^\d+\.\s|^---\s*$/m.test(message.content);

  return (
    <div className="py-2">
      <div className="flex items-start gap-2">
        <div className="flex-shrink-0 mt-0.5">
          <Logo size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[12px] font-semibold text-[var(--colony-text-secondary)]">{config.label}</span>
            <span className="text-[11px] text-[var(--colony-text-tertiary)]">{formatTime(message.timestamp)}</span>
          </div>
          <div className="text-[13px] text-[var(--colony-text-primary)] leading-[1.6]">
            {hasMarkdown ? (
              <SimpleMarkdown text={message.content} />
            ) : (
              <div className="whitespace-pre-wrap">{message.content}</div>
            )}
          </div>
          {message.codeBlocks?.map((block, i) => (
            <CodeBlock key={i} language={block.language} code={block.code} filename={block.filename} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Model Picker ────────────────────────────────────────────────────

function ModelPicker({
  selectedModel,
  onSelectModel,
  reasoningLevel,
  onReasoningChange,
}: {
  selectedModel: AIModel;
  onSelectModel: (model: AIModel) => void;
  reasoningLevel: ReasoningLevel;
  onReasoningChange: (level: ReasoningLevel) => void;
}) {
  const [open, setOpen] = useState(false);
  const config = getModelConfig(selectedModel);
  const SelectedLogo = config.Logo;
  const showReasoning = config.supportsReasoning;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-muted/30 transition-colors"
        >
          <SelectedLogo size={16} />
          <span className="text-[13px] font-medium text-muted-foreground">
            {config.shortLabel}
          </span>
          {showReasoning && reasoningLevel === "high" && (
            <Zap className="w-3 h-3 text-amber-500" />
          )}
          <ChevronDown className="w-3.5 h-3.5 text-muted-foreground/60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="top"
        className="w-64 p-0"
        sideOffset={8}
      >
        <div className="p-1.5">
          <div className="px-2 py-1 text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wider">
            Model
          </div>
          {selectableModels.map((modelId) => {
            const mc = getModelConfig(modelId);
            const Logo = mc.Logo;
            return (
              <button
                key={modelId}
                type="button"
                onClick={() => {
                  onSelectModel(modelId);
                  if (!mc.supportsReasoning && reasoningLevel === "high") {
                    onReasoningChange("standard");
                  }
                  setOpen(false);
                }}
                className={cn(
                  "w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-colors",
                  "hover:bg-muted/50",
                  modelId === selectedModel && "bg-muted/60"
                )}
              >
                <Logo size={16} />
                <span className="text-[13px] font-medium text-foreground/80 flex-1">
                  {mc.label}
                </span>
                {modelId === selectedModel && (
                  <Check className="w-3.5 h-3.5 text-primary" />
                )}
              </button>
            );
          })}
        </div>

        {/* Reasoning toggle */}
        <div className="border-t border-border/40 p-1.5">
          <div className="px-2 py-1 text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wider">
            Reasoning
          </div>
          {(["standard", "high"] as ReasoningLevel[]).map((level) => (
            <button
              key={level}
              type="button"
              disabled={level === "high" && !getModelConfig(selectedModel).supportsReasoning}
              onClick={() => onReasoningChange(level)}
              className={cn(
                "w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-colors",
                "hover:bg-muted/50",
                level === reasoningLevel && "bg-muted/60",
                level === "high" && !getModelConfig(selectedModel).supportsReasoning && "opacity-30 cursor-not-allowed"
              )}
            >
              {level === "high" ? (
                <Zap className="w-4 h-4 text-amber-500" />
              ) : (
                <div className="w-4 h-4 flex items-center justify-center">
                  <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40" />
                </div>
              )}
              <span className="text-[13px] font-medium text-foreground/80 flex-1 capitalize">
                {level}
              </span>
              {level === reasoningLevel && (
                <Check className="w-3.5 h-3.5 text-primary" />
              )}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ── Context Picker ──────────────────────────────────────────────────

function ContextPicker({
  contextItems,
  onAdd,
  onRemove,
}: {
  contextItems: ContextItem[];
  onAdd: (item: ContextItem) => void;
  onRemove: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const projects = useWorldStore((s) => s.projects);

  const availableProjects = projects.filter(
    (p) => !contextItems.some((c) => c.id === `project-${p.id}`)
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-muted/30 transition-colors"
        >
          <File className="w-4 h-4 text-muted-foreground/60" />
          <span className="text-[13px] font-medium text-muted-foreground">
            {contextItems.length > 0 ? `${contextItems.length} attached` : "context"}
          </span>
          <ChevronDown className="w-3.5 h-3.5 text-muted-foreground/60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="top"
        className="w-72 p-0"
        sideOffset={8}
      >
        {/* Currently attached */}
        {contextItems.length > 0 && (
          <div className="p-1.5 border-b border-border/40">
            <div className="px-2 py-1 text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wider">
              Attached
            </div>
            {contextItems.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-2 px-2 py-1.5 rounded-md group"
              >
                {item.type === "project" ? (
                  <FolderOpen className="w-3.5 h-3.5 text-muted-foreground/60" />
                ) : (
                  <FileCode className="w-3.5 h-3.5 text-muted-foreground/60" />
                )}
                <span className="text-[13px] font-medium text-foreground/80 flex-1 truncate">
                  {item.label}
                </span>
                <button
                  type="button"
                  onClick={() => onRemove(item.id)}
                  aria-label={`Remove ${item.label}`}
                  className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-muted/50 transition-all"
                >
                  <X className="w-3 h-3 text-muted-foreground/60" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Available projects to attach */}
        <div className="p-1.5">
          <div className="px-2 py-1 text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wider">
            Projects
          </div>
          {availableProjects.length === 0 ? (
            <div className="px-2 py-3 text-center text-xs text-muted-foreground/50">
              {projects.length === 0 ? "No projects yet" : "All projects attached"}
            </div>
          ) : (
            availableProjects.map((project) => (
              <button
                key={project.id}
                type="button"
                onClick={() => {
                  onAdd({
                    id: `project-${project.id}`,
                    type: "project",
                    label: project.name,
                  });
                }}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left hover:bg-muted/50 transition-colors"
              >
                <FolderOpen className="w-3.5 h-3.5 text-muted-foreground/60" />
                <span className="text-[13px] font-medium text-foreground/80 flex-1 truncate">
                  {project.name}
                </span>
              </button>
            ))
          )}
        </div>

        <div className="border-t border-border/40 p-2">
          <p className="text-[10px] text-muted-foreground/50 leading-relaxed">
            Attach project context so the agent has access to repo info, CLAUDE.md, and file structure.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ── Provider → AIModel mapping ──────────────────────────────────────

export function workflowModelToAIModel(provider?: string, model?: string): AIModel {
  if (!provider) return "claude-code";
  if (provider === "codex") {
    if (model?.includes("5.3")) return "codex-5.3";
    return "codex-5.3";
  }
  if (provider === "claude") {
    if (model?.includes("opus-4-6") || model?.includes("opus-4.6")) return "claude-opus-4.6";
    if (model?.includes("sonnet-4-5") || model?.includes("sonnet-4.5")) return "claude-sonnet-4.5";
    if (model?.includes("haiku")) return "claude-haiku-4.5";
    return "claude-opus-4.6";
  }
  if (provider === "gemini") {
    if (model?.includes("flash")) return "gemini-2.5-flash";
    return "gemini-2.5-pro";
  }
  return "claude-code";
}

// ── Message classification ───────────────────────────────────────────

function isLLMReviewMessage(msg: ChatMessage): boolean {
  if (msg.displayType !== "activity") return false;
  const nodeId = msg.activityMeta?.nodeId;
  const nodeLabel = msg.activityMeta?.nodeLabel;
  return nodeId === "llm_review" || (nodeLabel?.toLowerCase().includes("review") ?? false);
}

// Patterns that indicate CLI/terminal output (should go in output box).
// Only strong signals — bracket prefixes, shell prompts, progress indicators.
// Gerund verbs (reading, writing…) removed: they match AI natural language too often.
const CLI_OUTPUT_PATTERNS = [
  /^\[exec\]/i,
  /^\[git\]/i,
  /^\[npm\]/i,
  /^\[pip\]/i,
  /^\[yarn\]/i,
  /^\[cmd\]/i,
  /^\[shell\]/i,
  /^\[bash\]/i,
  /^\[claude\]/i,
  /^\[info\]/i,
  /^\[debug\]/i,
  /^\[warn\]/i,
  /^\[error\]/i,
  /^claude still running/i,
  /^still running/i,
  /\(\d+s\)\s*$/,  // ends with (123s)
  /^\s*\d+%/,      // starts with percentage
  /^>/,            // shell prompt style
  /^\$/,           // bash prompt style
];

function looksLikeCliOutput(content: string): boolean {
  const trimmed = content.trim();
  if (!trimmed) return false;

  // Strong CLI patterns (bracket prefixes, shell prompts, time suffixes)
  if (CLI_OUTPUT_PATTERNS.some(pattern => pattern.test(trimmed))) {
    return true;
  }

  // Short single-line status messages with clear CLI indicators
  const lines = trimmed.split('\n');
  if (trimmed.length < 100 && lines.length === 1) {
    // Contains brackets like [exec] or timestamps like (150s)
    if (/\[.+\]/.test(trimmed) || /\(\d+s?\)/.test(trimmed)) {
      return true;
    }
    // Very short messages with "..." that don't look like natural language
    // (max 3 words before "..." — e.g. "installing deps..." but not "I'm reading the file...")
    if (trimmed.length < 60 && /\.{3}\s*$/.test(trimmed)) {
      const beforeDots = trimmed.replace(/\.{3,}\s*$/, "").trim();
      if (beforeDots.split(/\s+/).length <= 3) {
        return true;
      }
    }
  }

  return false;
}

// AI-type nodes whose output should be chat, not boxes
const AI_NODE_TYPES = new Set(["plan", "code", "subtasks", "llm_review"]);

// Check if an activity message is from an AI-type node.
// Works with both new data (has nodeType) and old data (check nodeLabel, nodeId).
function isFromAiNode(meta?: { nodeId?: string; nodeType?: string; nodeLabel?: string }): boolean {
  if (!meta) return false;
  // Direct nodeType match (new events)
  if (meta.nodeType && AI_NODE_TYPES.has(meta.nodeType)) return true;
  // nodeId might match type name (hydrated data, some workflows)
  if (meta.nodeId && AI_NODE_TYPES.has(meta.nodeId)) return true;
  // Check label for common AI node labels
  const label = (meta.nodeLabel || "").toLowerCase();
  if (label.includes("code") || label.includes("plan") || label.includes("review") || label.includes("subtask")) return true;
  return false;
}

// Parse JSON review output into readable markdown
function tryFormatReviewJson(raw: string): string | null {
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    // Must look like review output (has verdict or summary)
    if (!parsed.verdict && !parsed.summary && !parsed.feedback) return null;
    const parts: string[] = [];
    if (parsed.verdict) {
      parts.push(`**Verdict: ${parsed.verdict.toUpperCase()}**`);
    }
    if (parsed.summary) {
      parts.push(parsed.summary);
    }
    if (Array.isArray(parsed.feedback) && parsed.feedback.length > 0) {
      parts.push("**Feedback:**");
      for (const item of parsed.feedback) {
        if (typeof item === "string") {
          parts.push(`- ${item}`);
        } else if (item?.message) {
          const prefix = item.severity ? `[${item.severity}] ` : "";
          const file = item.file ? ` (${item.file})` : "";
          parts.push(`- ${prefix}${item.message}${file}`);
        }
      }
    }
    return parts.length > 0 ? parts.join("\n\n") : null;
  } catch {
    return null;
  }
}

function isOutputMessage(msg: ChatMessage): boolean {
  if (msg.displayType !== "activity") return false;

  // LLM review should never be output box
  if (isLLMReviewMessage(msg)) return false;

  const variant = msg.activityMeta?.variant;
  const source = msg.activityMeta?.source;
  const nodeId = msg.activityMeta?.nodeId;
  const activityType = msg.activityMeta?.activityType;
  const content = msg.content || msg.activityMeta?.title || "";
  const model = msg.model || "";

  // AI agent messages (role=assistant) should be chat bubbles, not boxes
  if (msg.role === "assistant") return false;

  // Output from worker progress/log — but NOT from AI-type nodes
  if ((variant === "output" || variant === "status") &&
      (source === "worker-progress" || source === "worker-log")) {
    // AI node completed output should flow through as chat (has codeBlocks with the prose)
    if (isFromAiNode(msg.activityMeta) && variant === "output") {
      return false;
    }
    return true;
  }

  // Progress messages with output variant (from workflow hydration)
  if (variant === "output" && activityType === "progress") {
    return true;
  }

  // Content that looks like CLI output
  if (looksLikeCliOutput(content)) {
    return true;
  }

  // Node completion/status messages with output content (non-AI nodes only)
  if (variant === "output" && nodeId && !isFromAiNode(msg.activityMeta)) {
    return true;
  }

  // Progress/status activity types → output box
  if (activityType === "progress" || variant === "status") {
    return true;
  }

  return false;
}

// ── Status hint detection ─────────────────────────────────────────

// Short progress messages like "claude still running... (45s)" — show inline, not in box
function isStatusHintMessage(msg: ChatMessage): boolean {
  if (msg.displayType !== "activity") return false;
  const content = (msg.content || msg.activityMeta?.title || "").trim();
  if (!content || content.length > 120) return false;
  // "still running... (Ns)" or "waiting... (Ns)" style messages
  if (/still running|waiting for/i.test(content) && /\(\d+s\)/.test(content)) return true;
  // Single-line short status with time indicator and no real content
  if (content.split("\n").length === 1 && /\(\d+s\)\s*$/.test(content) && content.length < 80) return true;
  return false;
}

function StatusHint({ message, isLast = false }: { message: ChatMessage; isLast?: boolean }) {
  const content = (message.content || message.activityMeta?.title || "").trim();
  return (
    <div className="py-0.5 px-2">
      <span
        className="text-[12px] italic"
        style={{
          color: "var(--colony-text-tertiary)",
          opacity: isLast ? undefined : 0.5,
          animation: isLast ? "statusHintPulse 3s ease-in-out infinite" : "none",
        }}
      >
        {content}
      </span>
      {isLast && (
        <style>{`
          @keyframes statusHintPulse {
            0%, 100% { opacity: 0.45; }
            50% { opacity: 0.75; }
          }
        `}</style>
      )}
    </div>
  );
}

// ── Message grouping ────────────────────────────────────────────────

type GroupedItem = { type: "single"; message: ChatMessage }
                 | { type: "grouped"; messages: ChatMessage[] }
                 | { type: "llm-review"; message: ChatMessage }
                 | { type: "status-hint"; message: ChatMessage };

// Get a grouping key for output messages (same key = can be grouped together)
function getOutputGroupKey(msg: ChatMessage): string {
  const nodeId = msg.activityMeta?.nodeId || "";
  const source = msg.activityMeta?.source || "";
  const model = msg.model || "";

  // Group by node first (most specific)
  if (nodeId) return `node:${nodeId}`;
  // Then by source
  if (source) return `source:${source}`;
  // Then by model
  if (model === "claude-code") return "claude-code";
  // Default: all CLI-like output groups together
  return "cli-output";
}

function groupMessages(messages: ChatMessage[]): GroupedItem[] {
  const result: GroupedItem[] = [];
  let currentGroup: ChatMessage[] = [];
  let currentGroupKey: string | null = null;

  const flushGroup = () => {
    if (currentGroup.length === 0) return;
    result.push({ type: "grouped", messages: [...currentGroup] });
    currentGroup = [];
    currentGroupKey = null;
  };

  for (const msg of messages) {
    // LLM Review → special chat bubble (not output box)
    if (isLLMReviewMessage(msg)) {
      flushGroup();
      result.push({ type: "llm-review", message: msg });
      continue;
    }

    // Status hints (e.g. "claude still running... (45s)") → subtle inline text
    if (isStatusHintMessage(msg)) {
      flushGroup();
      result.push({ type: "status-hint", message: msg });
      continue;
    }

    // Worker output → group into output box (only if same source)
    if (isOutputMessage(msg)) {
      const groupKey = getOutputGroupKey(msg);

      // If key changed, flush current group and start new one
      if (currentGroupKey !== null && currentGroupKey !== groupKey) {
        flushGroup();
      }

      currentGroup.push(msg);
      currentGroupKey = groupKey;
      continue;
    }

    // Everything else → single item (chat bubble)
    flushGroup();
    result.push({ type: "single", message: msg });
  }

  flushGroup();
  return result;
}

// ── Main Conversation Component ─────────────────────────────────────

interface AIConversationProps {
  threads: ChatThread[];
  sessionId?: string;
  runTitle: string;
  isRunning?: boolean;
  onSendMessage?: (message: string) => void;
  onPauseResume?: () => void;
  runAge?: string;
  filesChanged?: number;
  additions?: number;
  deletions?: number;
  modelsUsed?: string[];
}

export function AIConversation({
  threads,
  sessionId,
  runTitle,
  isRunning = false,
  onSendMessage,
  onPauseResume,
  runAge,
  filesChanged,
  additions,
  deletions,
  modelsUsed,
}: AIConversationProps) {
  const [userInput, setUserInput] = useState("");
  const [isPaused, setIsPaused] = useState(false);
  const [selectedModel, setSelectedModel] = useState<AIModel>("claude-opus-4.6");
  const [reasoningLevel, setReasoningLevel] = useState<ReasoningLevel>("standard");
  const [contextItems, setContextItems] = useState<ContextItem[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);

  const session = sessionId ? useAiChatStore(s => s.sessions.get(sessionId)) : undefined;
  const eventTsBySeq = useMemo(() => {
    if (!session) return new Map<number, string>();
    return new Map(session.events.map((evt) => [evt.seq, evt.ts]));
  }, [session]);

  const sessionMessages: ChatMessage[] = session
    ? session.messages.map((m) => ({
        id: m.id,
        role: m.role as ChatMessageRole,
        model: (session.provider as AIModel) || "claude-code",
        content: m.content,
        timestamp:
          eventTsBySeq.get(m.eventSeqRange[0]) ||
          eventTsBySeq.get(m.eventSeqRange[1]) ||
          session.events[0]?.ts ||
          new Date(0).toISOString(),
      }))
    : [];

  const threadMessages = threads.flatMap(t => t.messages)
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  const allMessages = sessionId && session
    ? [...sessionMessages, ...threadMessages.filter(m => m.displayType === "activity")]
        .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
    : threadMessages;

  const isInteractive = !session || session.provider === "claude-code";

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [allMessages.length]);

  const handleSend = () => {
    const text = inputRef.current?.textContent?.trim() || "";
    if (text && onSendMessage) {
      onSendMessage(text);
      if (inputRef.current) {
        inputRef.current.textContent = "";
      }
      setUserInput("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handlePauseResume = () => {
    setIsPaused(!isPaused);
    onPauseResume?.();
  };

  const handleInput = () => {
    setUserInput(inputRef.current?.textContent || "");
  };

  const addContextItem = (item: ContextItem) => {
    setContextItems((prev) => [...prev, item]);
  };

  const removeContextItem = (id: string) => {
    setContextItems((prev) => prev.filter((c) => c.id !== id));
  };

  return (
    <div className="h-full flex flex-col bg-background">
      <div className="px-4 md:px-8 lg:px-16 xl:px-24 py-3 flex-shrink-0 border-b border-[var(--colony-border)]">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-1.5">
            <h2 className="text-[15px] font-semibold text-[var(--colony-text-heading)] tracking-[-0.02em] truncate">{runTitle}</h2>
            {isRunning && (
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 h-[20px] px-2 rounded-full bg-[rgba(59,130,246,0.1)] text-[11px] font-medium text-[var(--colony-blue)] whitespace-nowrap shrink-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--colony-blue)] animate-pulse" />
                  Running
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handlePauseResume}
                  className="h-6 px-2 gap-1 text-xs text-[var(--colony-text-tertiary)] hover:text-[var(--colony-text-primary)]"
                >
                  {isPaused ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
                </Button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3 text-[var(--colony-text-tertiary)]">
            {runAge && <span className="text-[12px]">{runAge}</span>}
            {(additions !== undefined || deletions !== undefined) && (
              <>
                {runAge && <span className="text-[11px]">·</span>}
                <span className="text-[12px] font-mono tabular-nums">
                  <span style={{ color: "var(--colony-green)" }}>+{additions ?? 0}</span>
                  <span className="mx-0.5 text-[var(--colony-text-tertiary)]">/</span>
                  <span style={{ color: "var(--colony-red)" }}>-{deletions ?? 0}</span>
                </span>
              </>
            )}
            {filesChanged !== undefined && (
              <>
                <span className="text-[11px]">·</span>
                <span className="text-[12px]">{filesChanged} files</span>
              </>
            )}
            {modelsUsed && modelsUsed.length > 0 && (
              <>
                <span className="text-[11px]">·</span>
                <span className="text-[12px]">{modelsUsed.join(", ")}</span>
              </>
            )}
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1" ref={scrollRef}>
        <div className="py-4 px-4 md:px-8 lg:px-16 xl:px-24">
          <div className="max-w-4xl mx-auto">
            {allMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-muted-foreground/40">
                <Sparkles className="w-8 h-8 mb-4" />
                <p className="text-sm font-medium">No conversation yet</p>
                <p className="text-xs mt-1">AI agents will start chatting when the run begins</p>
              </div>
            ) : (
              (() => {
                const grouped = groupMessages(allMessages);
                return grouped.map((item, idx) => {
                // Grouped output messages → output box
                if (item.type === "grouped") {
                  return <GroupedActivityBox key={item.messages[0].id} messages={item.messages} />;
                }

                // Status hint → subtle grey inline text (only animate the last one)
                if (item.type === "status-hint") {
                  return <StatusHint key={item.message.id} message={item.message} isLast={idx === grouped.length - 1} />;
                }

                // LLM Review → chat bubble from configured model (default: codex-5.3)
                if (item.type === "llm-review") {
                  const msg = item.message;
                  const meta = msg.activityMeta;
                  const codeBlocks = msg.codeBlocks ?? [];

                  // Extract text content from codeBlocks or message
                  const textFromBlocks = codeBlocks
                    .filter((b) => b.language === "text" || b.language === "markdown")
                    .map((b) => b.code)
                    .join("\n\n");
                  const textContent = textFromBlocks || meta?.details || msg.content;

                  if (!textContent?.trim()) return null;

                  // Use codex-5.3 as default for LLM review (workflow-configured model)
                  const reviewModel = msg.model || "codex-5.3";

                  return (
                    <MessageBubble
                      key={msg.id}
                      message={{
                        ...msg,
                        role: "assistant",
                        model: reviewModel as AIModel,
                        content: textContent,
                        codeBlocks: undefined,
                        displayType: "bubble",
                      }}
                    />
                  );
                }

                // Single message
                const msg = item.message;

                if (msg.displayType !== "activity") {
                  return <MessageBubble key={msg.id} message={msg} />;
                }

                const meta = msg.activityMeta;
                const codeBlocks = msg.codeBlocks ?? [];
                const hasTextContent = !!(meta?.details || msg.content?.trim());
                const textContent = meta?.details || msg.content || "";
                const isAiNode = isFromAiNode(meta);

                // AI-type node output → always try to render as chat bubble
                if (isAiNode && (codeBlocks.length > 0 || hasTextContent)) {
                  // Extract text from ALL codeBlocks (any language), try JSON review parsing
                  const allBlockText = codeBlocks.map((b) => b.code).join("\n\n");
                  const reviewParsed = allBlockText ? tryFormatReviewJson(allBlockText) : null;
                  const finalText = reviewParsed || allBlockText || meta?.details || msg.content;

                  if (finalText?.trim()) {
                    return (
                      <MessageBubble
                        key={msg.id}
                        message={{
                          ...msg,
                          role: "assistant",
                          model: msg.model || "claude-code",
                          content: finalText,
                          codeBlocks: undefined,
                          displayType: "bubble",
                        }}
                      />
                    );
                  }
                }

                // Determine if code blocks are actually code (not plain text output)
                const hasTechnicalBlocks = codeBlocks.length > 0 && codeBlocks.some(
                  (b) => b.language !== "text" && b.language !== "markdown"
                );

                // Code/bash/diff/json output → ActivityBox (clean code block)
                if (hasTechnicalBlocks) {
                  // Still try JSON review parsing for codeBlocks that look like review output
                  const jsonBlocks = codeBlocks.filter((b) => b.language === "json");
                  for (const jb of jsonBlocks) {
                    const parsed = tryFormatReviewJson(jb.code);
                    if (parsed) {
                      return (
                        <MessageBubble
                          key={msg.id}
                          message={{
                            ...msg,
                            role: "assistant",
                            model: msg.model || "claude-code",
                            content: parsed,
                            codeBlocks: undefined,
                            displayType: "bubble",
                          }}
                        />
                      );
                    }
                  }
                  return <ActivityBox key={msg.id} message={msg} />;
                }

                // CLI-like output → render as single GroupedActivityBox
                if (looksLikeCliOutput(textContent)) {
                  return <GroupedActivityBox key={msg.id} messages={[msg]} />;
                }

                // Plain text output (including text wrapped in codeBlocks) → chat bubble
                if (codeBlocks.length > 0 || hasTextContent) {
                  const textFromBlocks = codeBlocks
                    .filter((b) => b.language === "text" || b.language === "markdown")
                    .map((b) => b.code)
                    .join("\n\n");
                  const finalTextContent = textFromBlocks || meta?.details || msg.content;

                  if (finalTextContent?.trim()) {
                    return (
                      <MessageBubble
                        key={msg.id}
                        message={{
                          ...msg,
                          role: "assistant",
                          model: msg.model || "claude-code",
                          content: finalTextContent,
                          codeBlocks: undefined,
                          displayType: "bubble",
                        }}
                      />
                    );
                  }
                }

                // Status-only with no content → skip
                return null;
              });
              })()
            )}
          </div>
        </div>
      </ScrollArea>

      {(isInteractive || !sessionId) && (
        <div className="px-4 md:px-8 lg:px-16 xl:px-24 py-4">
          {/* Context chips */}
          {contextItems.length > 0 && (
            <div className="max-w-4xl mx-auto flex flex-wrap gap-1.5 mb-2">
              {contextItems.map((item) => (
                <span
                  key={item.id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 border border-primary/20 text-[11px] font-medium text-foreground/70"
                >
                  {item.type === "project" ? (
                    <FolderOpen className="w-3 h-3" />
                  ) : (
                    <FileCode className="w-3 h-3" />
                  )}
                  {item.label}
                  <button
                    type="button"
                    onClick={() => removeContextItem(item.id)}
                    aria-label={`Remove ${item.label}`}
                    className="ml-0.5 hover:text-foreground transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="max-w-4xl mx-auto rounded-2xl overflow-hidden bg-muted/50 dark:bg-zinc-800/80 border border-border dark:border-zinc-700/50">
            <div
              ref={inputRef}
              contentEditable
              onInput={handleInput}
              onKeyDown={handleKeyDown}
              data-placeholder="Plan, @ for context, / for commands"
              className="w-full px-4 pt-4 pb-2 text-[15px] outline-none min-h-[40px] max-h-[120px] overflow-y-auto text-foreground/90 empty:before:content-[attr(data-placeholder)] empty:before:text-muted-foreground/50"
            />

            <div className="flex items-center gap-0.5 px-2 pb-2">
              <ModelPicker
                selectedModel={selectedModel}
                onSelectModel={setSelectedModel}
                reasoningLevel={reasoningLevel}
                onReasoningChange={setReasoningLevel}
              />

              <ContextPicker
                contextItems={contextItems}
                onAdd={addContextItem}
                onRemove={removeContextItem}
              />

              <div className="flex-1" />

              <button
                type="button"
                onClick={handleSend}
                disabled={!userInput.trim()}
                className="p-2 rounded-lg hover:bg-muted/30 transition-colors disabled:opacity-30"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-muted-foreground">
                  <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function AIConversationEmpty() {
  return (
    <div className="h-full flex items-center justify-center">
      <div className="text-center max-w-sm">
        <MessageCircle className="w-10 h-10 mx-auto mb-3 text-muted-foreground/30" />
        <p className="text-sm font-medium text-muted-foreground mb-1">
          Select a run to view the conversation
        </p>
        <p className="text-xs text-muted-foreground/60">
          Choose a task from the left panel to see the AI agent's
          work, including plans, code changes, and test results.
        </p>
      </div>
    </div>
  );
}
